import { CommonPageWrapper, HeadingWrapper } from 'admin/components/wrappers';
import { AdminSearch } from 'app/components/AdminSearchField';
import CustomBreadCrumb from 'app/components/CustomBreadCrumb';
import { PageMainLabel } from 'app/components/SHLabel';
import { TableSkeletonLoader } from 'app/components/TableSkeletonLoader';
import { usePermission } from 'hooks/usePermission';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import BackNumberTable from './BackNumberTable';
import { useDebounce } from 'hooks/useDebounce';
import useSettingsApi from 'admin/hooks/useSettingsApi';
import AdminCustomButton from 'app/components/AdminCustomButton';
import { AdminCustomPopUp } from 'app/components/AdminCustomPopUp';
import CustomDataGrid from 'app/components/CustomDataGrid/Index';
import BackNoCell from 'app/components/CustomDataGrid/Cells/BackNoCell';
import RiderNameCell from 'app/components/CustomDataGrid/Cells/RiderName';
import StatusChip from 'app/components/StatusChip';
import { AdminTextField, Errortext } from 'app/components/AdminTextField';
import { BacknumbersRange } from 'services/openapi';
import SimpleReactValidator from 'simple-react-validator';
import { CommonLoader } from 'app/components/CommonLoader';
import AdminDropDown from 'app/components/AdminDropDown';
import { GeneralLookup } from 'services/openapi';

type Props = {};
const breadCrumbItems = [
    {
        id: '',
        text: 'Showyeda admin',
        disabled: true,
    },
    {
        id: '/admin/settings',
        text: 'Settings',
    },
    {
        id: '',
        text: 'Back Number Management',
        disabled: true,
    },
];
const BackNumberManagement = (props: Props) => {
    const { hasViewPermission } = usePermission();
    const {
        fetchAdminBacknumberList,
        makeBacknumberAvailable,
        loading,
        backnumberList,
        fetchAllRiderByBackNumber,
        allRiderByBackNumberList,
        setAllRiderByBackNumberList,
        isPopupLoading,
        addRangeBackNumbers,
        isAddBackNumber,
        setIsAddBackNumber,
        backNumberLoading,
    } = useSettingsApi();
    const [pageSkip, setPageSkip] = useState<number | undefined>(0);
    const [pageSize, setPageSize] = useState<number>(50);
    const [pageSizeSelected, setPageSizeSelected] = useState<GeneralLookup>({
        id: '50',
        name: '50',
    });
    const pageSizeOptions: GeneralLookup[] = [
        { id: '25', name: '25' },
        { id: '50', name: '50' },
        { id: '100', name: '100' },
        { id: '200', name: '200' },
    ];
    const [addBackNumberRange, setAddBackNumberRange] =
        useState<BacknumbersRange>();
    const [validationError, setValidationError] = useState<string>('');
    const simpleValidator = useRef(new SimpleReactValidator());
    const [searchTerm, setSearchTerm] = useState<string>('');
    const debounceRiderSearch = useDebounce(searchTerm);
    const [isRiderBackNumberSearch, setIsRiderBackNumberSearch] =
        useState<boolean>(false);
    const [searchTermBackNumber, setSearchTermBackNumber] = useState<string>('');
    const [toggleSearch, setToggleSearch] = React.useState<boolean>(false);
    const [pageSkipBackNumberForAll, setPageSkipBackNumberForAll] = useState<
        number | undefined
    >(0);
    const [pageSizeBackNumberForAll, setPageSizeBackNumberForAll] =
        useState<number>(10);
    const [
        pageSizeBackNumberForAllSelected,
        setPageSizeBackNumberForAllSelected,
    ] = useState<GeneralLookup>({
        id: '10',
        name: '10',
    });
    const [forceUpdate, setForceUpdate] = useState(false);
    const handleSearch = e => {
        setSearchTerm(e.target.value);
    };

    const handlePageSizeChange = (data: GeneralLookup) => {
        const newPageSize = parseInt(data.id, 10);
        setPageSize(newPageSize);
        setPageSizeSelected(data);
        setPageSkip(0);
        fetchAdminBacknumberList(0, newPageSize, debounceRiderSearch);
    };

    const handlePageSizeBackNumberForAllChange = (data: GeneralLookup) => {
        const newPageSize = parseInt(data.id, 10);
        setPageSizeBackNumberForAll(newPageSize);
        setPageSizeBackNumberForAllSelected(data);
        setPageSkipBackNumberForAll(0);
        fetchAllRiderByBackNumber(0, newPageSize, searchTermBackNumber);
    };

    const RiderByBackNumberColumns = [
        {
            field: 'code',
            title: 'Back Number',
            cell: e => <BackNoCell e={e} title={e.dataItem.code} />,
            width: '150',
            sortable: true,
        },
        {
            field: 'rider',
            title: 'Rider Name',
            cell: e => (
                <RiderNameCell
                    e={e}
                    isHideView
                    title={
                        e?.dataItem?.rider?.firstName
                            ? e.dataItem?.rider?.firstName + ' ' + e.dataItem?.rider?.lastName
                            : '-'
                    }
                    profileClassName={e?.dataItem.rider ? '' : 'hide-image-style'}
                />
            ),
            width: '300',
            sortable: true,
            className: 'rider-name-style',
            headerClassName: 'rider-header-style',
        },
        {
            field: 'seasonName',
            title: 'Season',
            cell: e => (
                <RiderNameCell
                    e={e}
                    isHideView
                    title={e?.dataItem?.seasonName}
                    profileClassName={'hide-image-style'}
                />
            ),
            width: '300',
            sortable: true,
            className: 'rider-name-style',
            headerClassName: 'rider-header-style',
        },
        {
            field: 'isActive',
            title: 'Status',
            cell: e => (
                <td>
                    <StatusChip
                        text={e?.dataItem?.isActive ? 'Active' : 'Inactive'}
                        StatusType={e?.dataItem?.isActive ? 'active' : 'inactive'}
                    />
                </td>
            ),
            width: '150',
            sortable: false,
        },
    ];
    React.useEffect(() => {
        if (debounceRiderSearch.length >= 0) {
            if (pageSkip !== 0) {
                fetchAdminBacknumberList(0, pageSize, debounceRiderSearch);
                setPageSkip(0);
            } else {
                fetchAdminBacknumberList(pageSkip, pageSize, debounceRiderSearch);
            }
        } else {
            fetchAdminBacknumberList(pageSkip, pageSize, debounceRiderSearch);
        }
    }, [debounceRiderSearch, pageSize]);
    useEffect(() => {
        if (searchTermBackNumber === '') {
            setAllRiderByBackNumberList(undefined);
        }
    }, [searchTermBackNumber]);
    React.useEffect(() => {
        if (searchTermBackNumber) {
            fetchAllRiderByBackNumber(
                0,
                pageSizeBackNumberForAll,
                searchTermBackNumber,
            );
            setPageSkipBackNumberForAll(0);
        }
    }, [toggleSearch]);
    return (
        <>
            <CommonPageWrapper>
                <HeadingWrapper>
                    <PageMainLabel>Back Number Management</PageMainLabel>
                    <CustomBreadCrumb preData={breadCrumbItems} />
                </HeadingWrapper>
                <HeadingWrapper className="btn-wrapper">
                    <ButtonBackWrapper>
                        {hasViewPermission('0900101014') && (
                            <AdminCustomButton
                                buttonType="primary"
                                isActive={false}
                                className="fill-button button-style"
                                onClick={() => {
                                    setIsAddBackNumber(true);
                                    setAddBackNumberRange(undefined);
                                    setValidationError('');
                                }}
                            >
                                Add Back Number
                            </AdminCustomButton>
                        )}
                    </ButtonBackWrapper>
                    <ButtonWrapper>
                        <AdminCustomButton
                            buttonType="primary"
                            isActive={false}
                            className="fill-button button-style"
                            onClick={() => {
                                setIsRiderBackNumberSearch(true);
                            }}
                        >
                            Search Rider By Back Number
                        </AdminCustomButton>
                        <div style={{ width: '283px', paddingTop: '10px' }}>
                            <AdminSearch
                                placeholder="Search by name or back number"
                                onSearchTextEntering={handleSearch}
                                searchTerm={searchTerm}
                                setSearchTerm={setSearchTerm}
                            />
                        </div>
                    </ButtonWrapper>
                </HeadingWrapper>
                {(backnumberList?.pageDetails?.count ?? 0) > 10 && (
                    <EntriesPerPageDropDownWrapper>
                        <AdminDropDown
                            label="No: of entries per page"
                            onDataChange={(data: GeneralLookup) => {
                                handlePageSizeChange(data);
                            }}
                            defaultValue={pageSizeSelected}
                            data={pageSizeOptions}
                        />
                    </EntriesPerPageDropDownWrapper>
                )}
                {hasViewPermission('0900101012') && (
                    <>
                        {loading ? (
                            <TableSkeletonLoader
                                rowContent={8}
                                width={100}
                                headingWidth={80}
                            />
                        ) : (
                            <BackNumberTable
                                setPageSkip={setPageSkip}
                                pageSkip={pageSkip}
                                fetchAdminBacknumberList={fetchAdminBacknumberList}
                                debounceRiderSearch={debounceRiderSearch}
                                backnumberList={backnumberList}
                                makeBacknumberAvailable={makeBacknumberAvailable}
                                pageSize={pageSize}
                            />
                        )}
                    </>
                )}
            </CommonPageWrapper>
            <AdminCustomPopUp
                className="rider-by-back-number-popup-style"
                visible={isRiderBackNumberSearch}
                setVisible={setIsRiderBackNumberSearch}
                closeToggle={() => {
                    setIsRiderBackNumberSearch(false);
                    setSearchTermBackNumber('');
                    setPageSkipBackNumberForAll(0);
                }}
                defaultPopupFooter={false}
                align="centre"
                popupLabel="Back Number List"
                children={
                    <FlexWrapper>
                        <div className="search-wrapper">
                            <AdminSearch
                                placeholder="Search by back number"
                                onSearchTextEntering={e => {
                                    const numericValue = e.target.value.replace(/[^0-9]/g, '');

                                    if (numericValue === '') {
                                        setToggleSearch(!toggleSearch);
                                    }
                                    if (numericValue.length <= 4) {
                                        setSearchTermBackNumber(numericValue);
                                    }
                                }}
                                searchTerm={searchTermBackNumber}
                                setSearchTerm={setSearchTermBackNumber}
                                className="searchField-backNumber-style"
                                onSearch={() => {
                                    fetchAllRiderByBackNumber(
                                        0,
                                        pageSizeBackNumberForAll,
                                        searchTermBackNumber,
                                    );
                                    setPageSkipBackNumberForAll(0);
                                }}
                            />
                            <AdminCustomButton
                                buttonType="primary"
                                isActive={false}
                                className="fill-button button-style"
                                onClick={e => {
                                    if (searchTermBackNumber !== '') {
                                        fetchAllRiderByBackNumber(
                                            0,
                                            pageSizeBackNumberForAll,
                                            searchTermBackNumber,
                                        );
                                        setPageSkipBackNumberForAll(0);
                                    }
                                }}
                                disabled={searchTermBackNumber === '' ? true : false}
                            >
                                Search
                            </AdminCustomButton>
                        </div>
                        {isPopupLoading ? (
                            <TableSkeletonLoader
                                rowContent={8}
                                width={100}
                                headingWidth={80}
                            />
                        ) : (
                            <>
                                {searchTermBackNumber !== '' && (
                                    <CustomDataGrid
                                        loader={isPopupLoading}
                                        showPagination={
                                            (allRiderByBackNumberList?.pageDetails?.count ?? 0) >
                                            pageSizeBackNumberForAll
                                        }
                                        data={allRiderByBackNumberList?.backNumbers ?? []}
                                        columns={RiderByBackNumberColumns}
                                        pageSize={pageSizeBackNumberForAll}
                                        totalCount={
                                            allRiderByBackNumberList?.pageDetails?.count || 0
                                        }
                                        onPageChange={event => {
                                            setPageSkipBackNumberForAll(event.page.skip);
                                            fetchAllRiderByBackNumber(
                                                event.page.skip,
                                                pageSizeBackNumberForAll,
                                                searchTermBackNumber,
                                            );
                                        }}
                                        skip={pageSkipBackNumberForAll}
                                        sortable={{
                                            allowUnsort: false,
                                            mode: 'single',
                                        }}
                                    />
                                )}
                            </>
                        )}
                    </FlexWrapper>
                }
            />
            <AdminCustomPopUp
                className="add-back-number-popup-style"
                visible={isAddBackNumber}
                setVisible={setIsAddBackNumber}
                closeToggle={() => {
                    setIsAddBackNumber(false);
                    setSearchTermBackNumber('');
                    setPageSkipBackNumberForAll(0);
                }}
                defaultPopupFooter={false}
                align="centre"
                popupLabel="Add Back Number"
                children={
                    <>
                        {backNumberLoading ? (
                            <CommonLoader loaderType="login" />
                        ) : (
                            <FlexWrapper>
                                <AdminTextField
                                    validations="required|min:2|max:4"
                                    validator={simpleValidator.current}
                                    label="Start Number"
                                    value={
                                        addBackNumberRange?.start
                                            ? (addBackNumberRange?.start as number)
                                            : ''
                                    }
                                    type="number"
                                    onChange={e => {
                                        const value = e.target.value;
                                        if (/^\d*$/.test(value) && value.length <= 4) {
                                            setValidationError('');
                                            setAddBackNumberRange({
                                                ...addBackNumberRange,
                                                start: value === '' ? undefined : Number(value),
                                            });
                                        }
                                    }}
                                    onKeyDown={e => {
                                        // prevent -, +, e, E
                                        if (['-', '+', 'e', 'E', '.'].includes(e.key)) {
                                            e.preventDefault();
                                        }
                                    }}
                                />
                                <AdminTextField
                                    validations="required|min:2|max:4"
                                    validator={simpleValidator.current}
                                    label="End Number"
                                    value={
                                        addBackNumberRange?.end
                                            ? (addBackNumberRange?.end as number)
                                            : ''
                                    }
                                    type="number"
                                    min={
                                        addBackNumberRange?.start
                                            ? (addBackNumberRange?.start as number)
                                            : 0
                                    }
                                    onKeyDown={e => {
                                        // prevent -, +, e, E
                                        if (['-', '+', 'e', 'E', '.'].includes(e.key)) {
                                            e.preventDefault();
                                        }
                                    }}
                                    onChange={e => {
                                        const value = e.target.value;
                                        if (/^\d*$/.test(value) && value.length <= 4) {
                                            setValidationError('');
                                            setAddBackNumberRange({
                                                ...addBackNumberRange,
                                                end: value === '' ? undefined : Number(value),
                                            });
                                        }
                                    }}
                                />
                                {validationError && <Errortext>{validationError}</Errortext>}
                                <ButtonWrapper style={{ paddingTop: '10px' }}>
                                    <AdminCustomButton
                                        buttonType="primary"
                                        isActive={false}
                                        className="fill-button button-style"
                                        onClick={() => {
                                            let isFormValid = simpleValidator.current.allValid();
                                            const start = addBackNumberRange?.start;
                                            const end = addBackNumberRange?.end;
                                            // basic validation
                                            if (Number(start) > Number(end)) {
                                                setValidationError(
                                                    'Start number should not be greater than End number.',
                                                );
                                                return;
                                            }
                                            setValidationError('');
                                            if (isFormValid) {
                                                addRangeBackNumbers(addBackNumberRange, () =>
                                                    fetchAdminBacknumberList(
                                                        pageSkip,
                                                        10,
                                                        debounceRiderSearch,
                                                    ),
                                                );
                                            } else {
                                                setForceUpdate(!forceUpdate);
                                                simpleValidator.current.showMessages();
                                            }
                                        }}
                                    >
                                        Save
                                    </AdminCustomButton>
                                </ButtonWrapper>
                            </FlexWrapper>
                        )}
                    </>
                }
            />
        </>
    );
};

export default BackNumberManagement;
const ButtonWrapper = styled.div`
  display: flex;
  justify-content: end;
  width: 100%;
  gap: 10px;
  align-items: baseline;
  .button-style {
    button {
      max-width: unset !important;
      width: auto !important;
    }
  }
  .field-style {
    display: flex;
  }
`;
const ButtonBackWrapper = styled.div`
  width: 100%;
  gap: 10px;
  .button-style {
    button {
      width: auto !important;
    }
  }
  .field-style {
    display: flex;
  }
`;
const FlexWrapper = styled.div`
  padding: 10px 20px 15px 20px;
  height: calc(100% - 30px);

  .drop-down {
    padding: 6px 0 10px 0;
    .label {
      font-size: 12px !important;
      color: rgb(23, 23, 23) !important;
    }
    .k-input-value-text {
      font-size: 12px !important;
      color: rgb(23, 23, 23) !important;
    }
  }
`;
const EntriesPerPageDropDownWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding-top: 10px;
  .SHdropdown {
    width: 160px;
    .label {
      margin-bottom: 8px;
      font-size: 13px;
      color: #475467;
      font-weight: 500;
    }
    .k-dropdownlist {
      max-height: 38px;
      border-radius: 8px !important;
    }
    .k-input-inner {
      padding: 0 8px !important;
      font-size: 13px;
    }
    .k-input-value-text {
      margin: 4px 0;
      font-size: 13px !important;
      color: #1e1e1e;
    }
  }
`;
